import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { KeyInitiative } from 'partner360/entities/keyInitiatives'
import {
  IMdfActivity,
  IMdfActivityWithBusinessPriorities,
} from 'partner360/features/mdfActivities/model/types'

/**
 * Builds a lookup from business-priority uid (lower-cased) to its display name, so
 * key-initiative `BusinessPriorities` uids can be resolved to human-readable names.
 */
export const buildBusinessPriorityUidToNameMap = (
  kiBusinessPriorities: IKIBusinessPriority[]
): Map<string, string> =>
  new Map(kiBusinessPriorities.map((priority) => [priority.uid.toLocaleLowerCase(), priority.name]))

/**
 * Resolves a key initiative's `BusinessPriorities` (comma-separated uids) to the
 * matching business-priority names. Matching is case-insensitive; unknown uids are dropped.
 */
export const getInitiativeBusinessPriorityNames = (
  initiative: KeyInitiative | undefined,
  uidToName: Map<string, string>
): string[] => {
  if (!initiative?.BusinessPriorities) {
    return []
  }

  return initiative.BusinessPriorities.split(',')
    .map((uid) => uid.trim().toLocaleLowerCase())
    .filter(Boolean)
    .map((uid) => uidToName.get(uid))
    .filter((name): name is string => Boolean(name))
}

/**
 * Enriches each activity with the business priorities of its linked key initiative
 * (`activity.keyInitiativeId` → `KeyInitiative.EntryId`). Activities with no linked
 * initiative (or whose initiative has no resolvable priorities) get an empty list.
 */
export const mapActivitiesWithBusinessPriorities = (
  activities: IMdfActivity[],
  initiatives: KeyInitiative[],
  kiBusinessPriorities: IKIBusinessPriority[]
): IMdfActivityWithBusinessPriorities[] => {
  const uidToName = buildBusinessPriorityUidToNameMap(kiBusinessPriorities)
  const initiativesById = new Map(initiatives.map((initiative) => [initiative.EntryId, initiative]))

  return activities.map((activity) => ({
    ...activity,
    businessPriorities:
      activity.keyInitiativeId != null
        ? getInitiativeBusinessPriorityNames(
            initiativesById.get(activity.keyInitiativeId),
            uidToName
          )
        : [],
  }))
}
